import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import type { AppShellContext } from '../components/AppShell';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import Map from '../Map';
import AreaSection from './AreaSection';
import MetricsSection, { MetricSelection } from './MetricsSection';
import { POI_TYPES, poiTypeLabel } from './poiTypes';
import ScoreSection from './ScoreSection';
import SearchSection from './SearchSection';
import SearchResultsTable from './SearchResultsTable';
import { ParcelHit, ParcelSearchCriteria, ParcelSearchResult, SEARCH_PAGE, hitBounds, hitElement, searchParcels } from './ParcelSearchAPI';
import { Score, loadScores } from './scores';
import { ScoreResult, ScoringMetric, getScore, getScoringCatalog } from './MetricsAPI';
import ProjectSection, { ProjectLocation } from './ProjectSection';
import { useSelectedProject } from '../cbos/selectedProject';
import CbosProjectPicker from '../cbos/CbosProjectPicker';
import {
    AreaSelection,
    MAX_RADIUS_KM,
    areaOutlineElement,
    circleGeometry,
    geometryBounds,
} from './areaGeometry';
import { TileResult, getMetricTiles } from './MetricsAPI';
import { PARCEL_LABEL_LIMIT, ParcelResult, loadParcelsForArea, parcelElement } from './ParcelsAPI';
import { AMBIGUOUS_COLOR, brwLegend } from './brwScale';
import { BrwZoneResult, brwZoneElement, loadBrwZonesForArea } from './BrwZonesAPI';
import { LandUseResult, landUseColor, landUseElement, landUseLabel, loadLandUseForArea } from './LandUseAPI';
import {
    EarthquakeResult,
    PowerLayerResult,
    SITE_HAZARD_DRAW_ORDER,
    SiteHazardKey,
    SiteHazardResult,
    WayLayerResult,
    loadBridges,
    loadEarthquakes,
    loadSiteHazard,
    loadPowerUnits,
    loadWays,
} from './MapLayersAPI';
import { InkarResult, InkarSelection, levelNames, levelSummary, loadInkarIndicator } from './InkarAPI';
import { useAtom } from 'jotai';
import { selectedEntityAtom } from '../State';
import { pageBackground } from '../cbos/palette';
import { useBrandBase } from '../cbos/useBrandBase';
import { userAPI } from '../UserAPI';
import { EntityQuery, Filter, Query } from '@the-real-insight/tri-model';
import { sectionHint } from './optionStyles';

/** The floating boxes over the map — the metric scale, the magnitude bands, the land use. */
const LEGEND =
    'pointer-events-none absolute z-[500] min-w-[150px] rounded-xl border bg-background/95 p-3 shadow-lg';
const LEGEND_TITLE = 'mb-1.5 max-w-[220px] text-xs font-bold';
const LEGEND_ROW = 'flex items-center gap-2 py-px text-xs';
const LEGEND_SWATCH = 'h-3.5 w-3.5 shrink-0 rounded-sm border';

/**
 * Heatmap — a metric of the Zensus grid drawn over a chosen area.
 *
 * Left a chimney of four sections (project, area, metrics, scores), the rest of the screen a map.
 * The map is the portal's existing one: everything it draws arrives as layer elements, the tiles
 * already coloured by the server, so this page decides *what* is shown and never *how* a value maps
 * to a colour.
 */


/**
 * What the score could not use everywhere: variables with values on fewer than 95 % of the units,
 * and the server's own notes (caps, missing data). Said beside the count, because a pale map
 * that is pale for lack of data reads exactly like one that is pale on its merits.
 */
const scoreGaps = (result: ScoreResult, t: TFunction): string => {
    if (!result?.coverage) return '';
    const gaps = result.coverage
        .filter((entry) => entry.units > 0 && entry.withValue / entry.units < 0.95)
        .map((entry) => `${entry.label} ${Math.round((100 * entry.withValue) / entry.units)} %`);
    const parts = [
        ...(gaps.length ? [t('heatmap.scores.coverage', { list: gaps.join(', ') })] : []),
        ...(result.notes ?? []),
    ];
    return parts.length ? ` — ${parts.join(' ')}` : '';
};

export default function HeatmapPage() {
    const brand = useBrandBase();
    const { setTitle } = useOutletContext<AppShellContext>();
    const { t, i18n } = useTranslation();

    // Chosen in the CBOS sidebar, read from the address: one project for every screen of the area.
    const { projectId: selectedProjectId, project } = useSelectedProject();
    const inCbos = useLocation().pathname.startsWith('/cbos');
    const [locations, setLocations] = useState<ProjectLocation[]>([]);
    /** Additive, unlike the metric: several kinds of place can matter at once. */
    const [poiTypes, setPoiTypes] = useState<string[]>([]);
    const [pois, setPois] = useState<
        { key: string; lat: number; lon: number; label: string; icon: string; entity: any }[]
    >([]);
    const [area, setArea] = useState<AreaSelection | null>(null);
    // Zwei Reiter: die Heatmap (Kacheln, Ebenen, Scores) und die Grundstückssuche im selben Gebiet.
    const [mode, setMode] = useState<'heatmap' | 'search'>('heatmap');
    const [searchCriteria, setSearchCriteria] = useState<ParcelSearchCriteria | null>(null);
    const [searchResult, setSearchResult] = useState<ParcelSearchResult | null>(null);
    const [searching, setSearching] = useState(false);
    const [searchingMore, setSearchingMore] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [selectedHitId, setSelectedHitId] = useState<string | null>(null);
    const [focusBounds, setFocusBounds] = useState<[[number, number], [number, number]] | null>(null);
    const [metric, setMetric] = useState<MetricSelection | null>(null);
    const [scores, setScores] = useState<Score[]>([]);
    const [selectedScoreId, setSelectedScoreId] = useState<string | null>(null);
    // Den Score nur an den Liegenschaften des Projekts rechnen und zeigen, statt über dem Gebiet.
    const [onlyProject, setOnlyProject] = useState(false);
    const [catalog, setCatalog] = useState<ScoringMetric[]>([]);
    const [parameterValues, setParameterValues] = useState<Record<string, number>>({});

    // The catalogue is the vocabulary of a formula, so the editor needs it whether or not the
    // metric list has been opened.
    useEffect(() => {
        getScoringCatalog()
            .then(setCatalog)
            .catch((error: any) => console.error('Could not load the metric catalogue', error));
        loadScores()
            .then(setScores)
            .catch((error: any) => console.error('Could not load the scores', error));
    }, []);

    const selectedScore = useMemo(
        () => scores.find((score) => score.id === selectedScoreId) ?? null,
        [scores, selectedScoreId],
    );

    /** Der Score rechnet nur an den Liegenschaften des Projekts — ohne Gebiet, auch über das Land verteilt. */
    const atPlaces = onlyProject && !!selectedScore && locations.length > 0;

    const [tiles, setTiles] = useState<TileResult | null>(null);
    const [loadingTiles, setLoadingTiles] = useState(false);
    const [tileError, setTileError] = useState<string | null>(null);

    const [showParcels, setShowParcels] = useState(false);
    // Wie die Flurstücke gemalt werden, nicht ob — siehe MetricsSection.
    const [parcelsBrw, setParcelsBrw] = useState(false);
    const [parcels, setParcels] = useState<ParcelResult | null>(null);
    const [loadingParcels, setLoadingParcels] = useState(false);
    const [parcelError, setParcelError] = useState<string | null>(null);

    const [showBrwZones, setShowBrwZones] = useState(false);
    const [brwZones, setBrwZones] = useState<BrwZoneResult | null>(null);
    const [loadingBrwZones, setLoadingBrwZones] = useState(false);
    const [brwZonesError, setBrwZonesError] = useState<string | null>(null);
    const [showLandUse, setShowLandUse] = useState(false);
    const [landUse, setLandUse] = useState<LandUseResult | null>(null);
    const [loadingLandUse, setLoadingLandUse] = useState(false);
    const [landUseError, setLandUseError] = useState<string | null>(null);

    /**
     * Power infrastructure and earthquakes.
     *
     * Both are fetched whole and then drawn in part: the categories a reader
     * ticks change what is on the map, not what was asked for. One request per
     * area rather than one per category — the query is the expensive half, and
     * the answer already comes back sorted into categories.
     */
    const [powerCategories, setPowerCategories] = useState<string[]>([]);
    const [power, setPower] = useState<PowerLayerResult | null>(null);
    const [loadingPower, setLoadingPower] = useState(false);
    const [powerError, setPowerError] = useState<string | null>(null);

    /**
     * The INKAR indicator, drawn over administrative units.
     *
     * A metric of its own kind: it colours the map, so it takes the place of a
     * Zensus metric or a score rather than sitting on top of one.
     */
    const [inkarSelection, setInkarSelection] = useState<InkarSelection | null>(null);
    const [inkar, setInkar] = useState<InkarResult | null>(null);
    const [loadingInkar, setLoadingInkar] = useState(false);
    const [inkarError, setInkarError] = useState<string | null>(null);

    /** Roads and paths, per kind — one request per area, drawn per ticked kind. */
    const [wayCategories, setWayCategories] = useState<string[]>([]);
    const [ways, setWays] = useState<WayLayerResult | null>(null);
    const [loadingWays, setLoadingWays] = useState(false);
    const [wayError, setWayError] = useState<string | null>(null);

    /**
     * Bridges, per category.
     *
     * One request per area, all categories in it — unlike the ways, where the
     * choice decides the query. Thirty bridges in a window against thousands of
     * footways: there is nothing to save by asking separately.
     */
    const [bridgeCategories, setBridgeCategories] = useState<string[]>([]);
    const [bridges, setBridges] = useState<WayLayerResult | null>(null);
    const [loadingBridges, setLoadingBridges] = useState(false);
    const [bridgeError, setBridgeError] = useState<string | null>(null);

    const [showEarthquakes, setShowEarthquakes] = useState(false);
    const [earthquakes, setEarthquakes] = useState<EarthquakeResult | null>(null);
    const [loadingEarthquakes, setLoadingEarthquakes] = useState(false);
    const [earthquakeError, setEarthquakeError] = useState<string | null>(null);

    /** Schutzgebiete, Hochwasser, Starkregen: je Schalter ein Ergebnis, geladen erst wenn er an ist. */
    const [siteHazards, setSiteHazards] = useState<SiteHazardKey[]>([]);
    const [siteHazardResults, setSiteHazardResults] = useState<Partial<Record<SiteHazardKey, SiteHazardResult>>>({});
    const [siteHazardLoading, setSiteHazardLoading] = useState<SiteHazardKey[]>([]);
    const [siteHazardErrors, setSiteHazardErrors] = useState<Partial<Record<SiteHazardKey, string>>>({});

    const handleLocations = useCallback((next: ProjectLocation[]) => setLocations(next), []);

    /** The portal's own side panel, opened by putting an entity in it. */
    const [, setSelectedEntity] = useAtom(selectedEntityAtom);

    /**
     * Take everything off the map that the metric list put there.
     *
     * The area and the project stay: they are the question being asked, not an
     * answer drawn over it, and a reader clearing their layers almost never means
     * "and forget where I am looking". The score stays too — it has its own
     * section, and clearing it from another one would be a surprise.
     */
    const clearSelection = useCallback(() => {
        setMetric(null);
        setInkarSelection(null);
        setShowParcels(false);
        setShowBrwZones(false);
        setShowLandUse(false);
        setPowerCategories([]);
        setWayCategories([]);
        setBridgeCategories([]);
        setShowEarthquakes(false);
        setSiteHazards([]);
        setPoiTypes([]);
    }, []);

    /**
     * Clicking a property makes it the area: a 5 km circle around it. That is the widest the slider
     * offers, and the reader who clicks a marker is asking "what surrounds this?" — the largest
     * neighbourhood on offer is the useful first answer, and the slider narrows it from there.
     */
    const focusLocation = useCallback(
        (entity: any) => {
            if (!entity || !Number.isFinite(entity.lat) || !Number.isFinite(entity.lon)) return;

            // Not every building carries a street or a name; naming it by nothing would leave the
            // caption reading "— 5 km".
            const label =
                [entity.street, entity.streetNumber].filter(Boolean).join(' ') ||
                entity.name ||
                t('heatmap.area.property');
            setArea({
                kind: 'address',
                label: `${label} — ${MAX_RADIUS_KM} km`,
                geometry: circleGeometry(entity.lat, entity.lon, MAX_RADIUS_KM),
                centre: { lat: entity.lat, lon: entity.lon },
                radiusKm: MAX_RADIUS_KM,
            });
        },
        [t],
    );

    /**
     * What a click on the map does, which depends on what was clicked.
     *
     * A property of the project is a *place to look at*: clicking it sets the
     * area, which is the whole point of having the markers there. Everything else
     * — a point of interest, a power unit, a quake, a parcel — is a thing to read,
     * and opens the side panel the rest of the portal opens for a map element.
     *
     * The panel is the global one, driven by `selectedEntityAtom`; the map is told
     * not to open it itself (`hideOverlay`) so that the two kinds of click do not
     * both happen at once on a property.
     */
    const handleMapClick = useCallback(
        (entity: any) => {
            if (!entity) return;
            if (entity.projectLocation) {
                focusLocation(entity);
                return;
            }

            // Ein Treffer der Suche: auch seine Zeile in der Tabelle markieren.
            if (entity?._id && searchResult?.rows.some((hit) => hit._id === String(entity._id))) {
                setSelectedHitId(String(entity._id));
            }
            setSelectedEntity(entity);
        },
        [focusLocation, setSelectedEntity, searchResult],
    );

    // Leaflet sizes itself once, from the pixel height of its container. A percentage height
    // resolves against a flex parent that has no height of its own yet, so the map comes out zero
    // pixels tall and stays blank — the frame is measured instead and the number handed down.
    const frameRef = useRef<HTMLDivElement | null>(null);
    const [mapHeight, setMapHeight] = useState(600);

    useLayoutEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;

        const measure = () => setMapHeight(Math.max(320, Math.round(frame.clientHeight)));
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(frame);
        return () => observer.disconnect();
    }, []);

    // Tiles are asked for exactly when both halves of the question exist. Either alone is not a
    // query — an area without a metric has nothing to colour, a metric without an area has no
    // bounds and would mean "all of Germany".
    useEffect(() => {
        // An Liegenschaften braucht es kein Gebiet: sie können über das ganze Land verteilt sein.
        if (!atPlaces && (!area || (!metric && !selectedScore))) {
            setTiles(null);
            setTileError(null);
            return;
        }

        let cancelled = false;
        setLoadingTiles(true);
        setTileError(null);

        // Ein Score kostet Sekunden auf dem Server. Beim Ziehen am Radius oder an einem Regler wird
        // deshalb erst gerechnet, wenn die Hand kurz ruht, und eine überholte Anfrage abgebrochen —
        // sonst rechnet der Server jede Zwischenstellung zu Ende, bis ihm der Speicher ausgeht.
        const abort = new AbortController();
        const run = (): Promise<TileResult | ScoreResult> => atPlaces
            ? getScore({
                  places: locations.map((location) => ({ id: location.id, lat: location.lat, lon: location.lon })),
                  formula: selectedScore!.formula,
                  name: selectedScore!.name,
                  parameters: parameterValues,
                  granularity: selectedScore!.granularity ?? 'cell',
                  distribution: selectedScore!.distribution ?? 'buildings',
              }, abort.signal)
            : selectedScore
            ? getScore({
                  geometry: area!.geometry,
                  formula: selectedScore.formula,
                  name: selectedScore.name,
                  parameters: parameterValues,
                  granularity: selectedScore.granularity ?? 'cell',
                  distribution: selectedScore.distribution ?? 'buildings',
              }, abort.signal)
            : getMetricTiles({
                  geometry: area!.geometry,
                  theme: metric!.theme,
                  category: metric!.category,
              });

        const timer = window.setTimeout(
            () =>
                run()
                    .then((result) => {
                        if (!cancelled) setTiles(result);
                    })
                    .catch((error: any) => {
                        if (cancelled || error?.name === 'AbortError') return;
                        console.error('Could not load the metric tiles', error);
                        setTiles(null);
                        setTileError(error?.message ?? t('global.generalError'));
                    })
                    .finally(() => {
                        if (!cancelled) setLoadingTiles(false);
                    }),
            selectedScore ? 450 : 0,
        );

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            abort.abort();
        };
    }, [area, metric, selectedScore, parameterValues, atPlaces, locations, t]);

    useEffect(() => {
        if (!area || !showParcels) {
            setParcels(null);
            setParcelError(null);
            return;
        }

        let cancelled = false;
        setLoadingParcels(true);
        setParcelError(null);

        loadParcelsForArea(area.geometry, area.centre ?? null)
            .then((result) => {
                if (!cancelled) setParcels(result);
            })
            .catch((error: any) => {
                console.error('Could not load the parcels', error);
                if (!cancelled) {
                    setParcels(null);
                    setParcelError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingParcels(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, showParcels, t]);

    useEffect(() => {
        if (!area || !showBrwZones) {
            setBrwZones(null);
            setBrwZonesError(null);
            setLoadingBrwZones(false);
            return;
        }

        let cancelled = false;
        setLoadingBrwZones(true);
        setBrwZonesError(null);

        loadBrwZonesForArea(area.geometry, area.centre ?? null)
            .then((result) => {
                if (!cancelled) setBrwZones(result);
            })
            .catch((error: any) => {
                console.error('Could not load the Bodenrichtwert zones', error);
                if (!cancelled) {
                    setBrwZones(null);
                    setBrwZonesError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingBrwZones(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, showBrwZones, t]);

    useEffect(() => {
        if (!area || !showLandUse) {
            setLandUse(null);
            setLandUseError(null);
            setLoadingLandUse(false);
            return;
        }

        let cancelled = false;
        setLoadingLandUse(true);
        setLandUseError(null);

        loadLandUseForArea(area.geometry, area.centre ?? null)
            .then((result) => {
                if (!cancelled) setLandUse(result);
            })
            .catch((error: any) => {
                console.error('Could not load the land use', error);
                if (!cancelled) {
                    setLandUse(null);
                    setLandUseError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingLandUse(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, showLandUse, t]);

    // Asked once per area, not once per category: the categories only decide what is
    // drawn out of an answer that already carries all of them.
    useEffect(() => {
        if (!area || powerCategories.length === 0) {
            setPower(null);
            setPowerError(null);
            setLoadingPower(false);
            return;
        }

        let cancelled = false;
        setLoadingPower(true);
        setPowerError(null);

        loadPowerUnits(area.geometry)
            .then((result) => {
                if (!cancelled) setPower(result);
            })
            .catch((error: any) => {
                console.error('Could not load the power infrastructure', error);
                if (!cancelled) {
                    setPower(null);
                    setPowerError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingPower(false);
            });

        return () => {
            cancelled = true;
        };
        // Deliberately not on `powerCategories`: ticking a second category must not
        // re-run the query, only draw more of what is already here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, powerCategories.length > 0, t]);

    // Unlike the power units, this one *is* keyed on the choice: only the ticked
    // kinds are queried, so a new tick is a new question rather than a redraw.
    useEffect(() => {
        if (!area || wayCategories.length === 0) {
            setWays(null);
            setWayError(null);
            setLoadingWays(false);
            return;
        }

        let cancelled = false;
        setLoadingWays(true);
        setWayError(null);

        loadWays(area.geometry, wayCategories, i18n.language)
            .then((result) => {
                if (!cancelled) setWays(result);
            })
            .catch((error: any) => {
                console.error('Could not load the ways', error);
                if (!cancelled) {
                    setWays(null);
                    setWayError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingWays(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, wayCategories, i18n.language, t]);

    useEffect(() => {
        if (!area || bridgeCategories.length === 0) {
            setBridges(null);
            setBridgeError(null);
            setLoadingBridges(false);
            return;
        }

        let cancelled = false;
        setLoadingBridges(true);
        setBridgeError(null);

        loadBridges(area.geometry, i18n.language)
            .then((result) => {
                if (!cancelled) setBridges(result);
            })
            .catch((error: any) => {
                console.error('Could not load the bridges', error);
                if (!cancelled) {
                    setBridges(null);
                    setBridgeError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingBridges(false);
            });

        return () => {
            cancelled = true;
        };
        // Only on the *first* tick, not on every one: the answer already holds
        // every category, so a second tick draws more of what is here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, bridgeCategories.length > 0, i18n.language, t]);

    useEffect(() => {
        if (!area || !showEarthquakes) {
            setEarthquakes(null);
            setEarthquakeError(null);
            setLoadingEarthquakes(false);
            return;
        }

        let cancelled = false;
        setLoadingEarthquakes(true);
        setEarthquakeError(null);

        loadEarthquakes(area.geometry)
            .then((result) => {
                if (!cancelled) setEarthquakes(result);
            })
            .catch((error: any) => {
                console.error('Could not load the earthquakes', error);
                if (!cancelled) {
                    setEarthquakes(null);
                    setEarthquakeError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingEarthquakes(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, showEarthquakes, t]);

    const previousAreaRef = useRef(area);
    useEffect(() => {
        if (!area) {
            setSiteHazardResults({});
            setSiteHazardErrors({});
            setSiteHazardLoading([]);
            previousAreaRef.current = null;
            return;
        }
        
        // Ein neues Gebiet heißt: alles neu laden.
        if (previousAreaRef.current !== area) {
            setSiteHazardResults({});
            previousAreaRef.current = area;
        }
        
        let cancelled = false;
        // Abgeschaltete verwerfen, damit ein erneutes Einschalten neu lädt und nichts Veraltetes zeichnet.
        setSiteHazardResults((previous) =>
            Object.fromEntries(Object.entries(previous).filter(([key]) => siteHazards.includes(key as SiteHazardKey))),
        );
        const toLoad = siteHazards.filter((key) => !siteHazardResults[key]);
        if (toLoad.length === 0) return;
        setSiteHazardLoading((previous) => [...previous, ...toLoad.filter((key) => !previous.includes(key))]);
        toLoad.forEach((key) => {
            loadSiteHazard(key, area.geometry)
                .then((result) => {
                    if (cancelled) return;
                    setSiteHazardResults((previous) => ({ ...previous, [key]: result }));
                    setSiteHazardErrors((previous) => ({ ...previous, [key]: undefined }));
                })
                .catch((error: any) => {
                    console.error(`Could not load ${key}`, error);
                    if (!cancelled) {
                        setSiteHazardErrors((previous) => ({
                            ...previous,
                            [key]: error?.message ?? t('global.generalError'),
                        }));
                    }
                })
                .finally(() => {
                    if (!cancelled) setSiteHazardLoading((previous) => previous.filter((entry) => entry !== key));
                });
        });
        return () => {
            cancelled = true;
        };
        // Nur Gebiet und Auswahl lösen Laden aus; die Ergebnisse selbst nicht.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, siteHazards, t]);

    useEffect(() => {
        if (!area || !inkarSelection) {
            setInkar(null);
            setInkarError(null);
            setLoadingInkar(false);
            return;
        }

        let cancelled = false;
        setLoadingInkar(true);
        setInkarError(null);

        loadInkarIndicator(area.geometry, inkarSelection, i18n.language)
            .then((result) => {
                if (!cancelled) setInkar(result);
            })
            .catch((error: any) => {
                console.error('Could not load the INKAR indicator', error);
                if (!cancelled) {
                    setInkar(null);
                    setInkarError(error?.message ?? t('global.generalError'));
                }
            })
            .finally(() => {
                if (!cancelled) setLoadingInkar(false);
            });

        return () => {
            cancelled = true;
        };
    }, [area, inkarSelection, i18n.language, t]);

    const runSearch = useCallback(
        async (criteria: ParcelSearchCriteria) => {
            if (!area) return;
            setSearching(true);
            setSearchError(null);
            setSelectedHitId(null);
            setFocusBounds(null);
            setSearchCriteria(criteria);
            try {
                setSearchResult(await searchParcels(area.geometry, criteria));
            } catch (error: any) {
                setSearchResult(null);
                setSearchError(error?.message ?? String(error));
            } finally {
                setSearching(false);
            }
        },
        [area],
    );
    const searchMore = useCallback(async () => {
        if (!area || !searchCriteria || !searchResult) return;
        setSearchingMore(true);
        try {
            const next = await searchParcels(area.geometry, searchCriteria, searchResult.rows.length);
            setSearchResult({ ...searchResult, rows: [...searchResult.rows, ...next.rows] });
        } catch (error: any) {
            setSearchError(error?.message ?? String(error));
        } finally {
            setSearchingMore(false);
        }
    }, [area, searchCriteria, searchResult]);
    // Der Rahmen um die Treffer der ersten Seite — eine neue Suche ist eine neue Frage und rahmt neu, „weitere“ nicht.
    const firstPage = searchResult?.rows.slice(0, SEARCH_PAGE);
    const hitsFrame = useMemo(() => {
        if (!firstPage?.length) return null;
        const lats = firstPage.map((hit) => hit.centerLat).filter(Number.isFinite);
        const lons = firstPage.map((hit) => hit.centerLon).filter(Number.isFinite);
        if (!lats.length) return null;
        const pad = lats.length === 1 ? 0.003 : 0;
        return [
            [Math.min(...lats) - pad, Math.min(...lons) - pad],
            [Math.max(...lats) + pad, Math.max(...lons) + pad],
        ] as [[number, number], [number, number]];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchResult?.rows[0]?._id, searchResult?.rows.length && Math.min(searchResult.rows.length, SEARCH_PAGE)]);
    const selectHit = useCallback((hit: ParcelHit) => {
        setSelectedHitId(hit._id);
        setFocusBounds(hitBounds(hit));
    }, []);
    // Ein anderes Gebiet ist eine andere Frage: die alten Treffer gehören nicht mehr dazu.
    useEffect(() => {
        setSearchResult(null);
        setSelectedHitId(null);
        setFocusBounds(null);
    }, [area]);

    const mapData = useMemo(() => {
        const layers: Record<string, any> = {};

        // Order matters: the tiles are drawn first so the red outline of the area stays on top of
        // them, and the project markers on top of both.
        if (tiles && tiles.elements.length > 0) {
            layers.tiles = { elements: tiles.elements };
        }
        // Below everything: an INKAR indicator colours whole administrative units, so it is the
        // backdrop the rest is read against — the same place the Zensus tiles occupy, which is why
        // only one of the two can be chosen at a time.
        if (inkar && inkar.elements.length > 0) {
            layers.inkar = { elements: inkar.elements };
        }
        // Under the parcels: land use is a filled surface, the parcel grid is a set of lines over
        // it. The other order would put a translucent fill on top of the lines and grey them out.
        // Unter der Flächennutzung und unter dem Flurstücksraster: die Zonen sind die
        // Fläche, gegen die beides gelesen wird. Umgekehrt läge eine halbtransparente
        // Füllung über den Linien und graute sie aus.
        if (brwZones && brwZones.zones.length > 0) {
            layers.brwZones = {
                elements: brwZones.zones.map((zone) => brwZoneElement(zone)).filter(Boolean),
            };
        }
        if (landUse && landUse.areas.length > 0) {
            layers.landUse = {
                elements: landUse.areas.map((entry) => landUseElement(entry, i18n.language)).filter(Boolean),
            };
        }
        // Over the tiles, under the area outline: the cadastral grid is something to read the
        // colours through, and the boundary of the area has to stay the strongest line on screen.
        if (parcels && parcels.parcels.length > 0) {
            const withLabels = parcels.parcels.length <= PARCEL_LABEL_LIMIT;
            layers.parcels = {
                elements: parcels.parcels
                    .map((parcel) => parcelElement(parcel, withLabels, parcelsBrw))
                    .filter(Boolean),
            };
        }
        if (area) {
            layers.area = { elements: [areaOutlineElement(area)] };
        }
        if (locations.length > 0) {
            // Mit einem Score an den Liegenschaften trägt jeder Marker die Farbe seines Scores.
            const placeScores = new globalThis.Map(((tiles as ScoreResult | null)?.places ?? []).map((place) => [place.id, place] as const));
            layers.projectLocations = {
                elements: locations.map((location) => ({
                    // `address` is the one marker icon the map has for a street location; there is
                    // no building icon, and an unknown type falls back to a school pin.
                    display: {
                        type: 'poi',
                        poiType: 'address',
                        lat: location.lat,
                        lon: location.lon,
                        markerColor: placeScores.get(location.id)?.color ?? undefined,
                        tooltip:
                            placeScores.get(location.id)?.value != null
                                ? `${location.label} — ${tiles!.themeLabel}: ${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(placeScores.get(location.id)!.value!)}`
                                : location.label,
                    },
                    // The whole location rides along on the entity: the map hands it back on
                    // click, and that is what turns a property into the area to look at.
                    // `projectLocation` is what the click handler tells it apart by — every
                    // other marker on this map opens the side panel instead.
                    //
                    // Deliberately without `_type`: the map instantiates a typed entity when it
                    // sees one, and `new Building(...)` keeps only the model's own fields — lat and
                    // lon are not among them, so the click arrived without a position. The icon
                    // comes from `display.poiType` and is unaffected.
                    entity: { name: location.label, projectLocation: true, ...location },
                })),
            };
        }

        // Over the surfaces, under the markers: a road network is something to read
        // the rest against, and it must not bury the pins.
        if (ways) {
            wayCategories.forEach((category) => {
                const layer = ways.layers[category];
                if (layer?.elements?.length) {
                    layers[`ways:${category}`] = { elements: layer.elements };
                }
            });
        }
        // Over the ways: a bridge is a structure on top of the network, and it is
        // the thing being asked about when this layer is on.
        if (bridges) {
            bridgeCategories.forEach((category) => {
                const layer = bridges.layers[category];
                if (layer?.elements?.length) {
                    layers[`bridges:${category}`] = { elements: layer.elements };
                }
            });
        }
        // Only the ticked categories, out of the answer that holds all of them.
        if (power) {
            powerCategories.forEach((category) => {
                const layer = power.layers[category];
                if (layer?.elements?.length) {
                    layers[`power:${category}`] = { elements: layer.elements };
                }
            });
        }

        // Flächen unter den Erdbeben, in SITE_HAZARD_DRAW_ORDER: Gemeinden und Klimaraster zuunterst, Denkmäler obenauf.
        SITE_HAZARD_DRAW_ORDER.forEach((key) => {
            const result = siteHazardResults[key];
            if (siteHazards.includes(key) && result && result.elements.length > 0) {
                layers[`siteHazard:${key}`] = { elements: result.elements };
            }
        });

        // Over everything else: a quake is a circle several kilometres wide, and
        // under the markers it would be a coloured haze nobody can attribute.
        if (earthquakes && earthquakes.elements.length > 0) {
            layers.earthquakes = { elements: earthquakes.elements };
        }

        if (pois.length > 0) {
            // Only the kinds still ticked: the loaded set lags a deselection by one
            // query, and drawing it wholesale left markers on the map that the list
            // said were off.
            const shown = pois.filter((poi) => poiTypes.includes(poi.key));

            if (shown.length > 0) {
                layers.pointsOfInterest = {
                    elements: shown.map((poi) => ({
                        display: {
                            type: 'poi',
                            poiType: poi.icon,
                            lat: poi.lat,
                            lon: poi.lon,
                            tooltip: poi.label,
                        },
                        // The queried document itself, so a click has something to show:
                        // the side panel renders an entity's fields from its type, and a
                        // pair of coordinates has no type and no fields.
                        entity: poi.entity,
                    })),
                };
            }
        }

        // Im Suchreiter nur, worum es dort geht: die Treffer, das Gebiet, die Objekte des Projekts.
        if (mode === 'search') {
            const only: Record<string, any> = {};
            if (searchResult?.rows.length) {
                only.searchHits = {
                    elements: searchResult.rows.map((hit) => hitElement(hit, hit._id === selectedHitId)).filter(Boolean),
                };
            }
            if (layers.area) only.area = layers.area;
            return Object.keys(only).length > 0 ? { layers: only } : null;
        }

        return Object.keys(layers).length > 0 ? { layers } : null;
    }, [
        mode,
        searchResult,
        selectedHitId,
        tiles,
        inkar,
        parcels,
        brwZones,
        // WIE die Flurstücke gefärbt werden, nicht nur WELCHE geladen sind.
        //
        // Ohne diese Zeile setzt der Schalter "nach Bodenrichtwert" zwar den Zustand,
        // aber dieses Memo rechnet nicht neu — gezeichnet bleiben die Elemente, die mit
        // `parcelsBrw = false` entstanden sind. Der Schalter klickt, und nichts
        // passiert, in jedem Bundesland gleichermaßen. Von außen sieht das aus wie
        // fehlende Daten und schickt die Suche in die Pipeline statt hierher.
        parcelsBrw,
        landUse,
        power,
        powerCategories,
        ways,
        wayCategories,
        bridges,
        bridgeCategories,
        earthquakes,
        siteHazards,
        siteHazardResults,
        area,
        locations,
        pois,
        poiTypes,
        i18n.language,
    ]);

    /**
     * How many units are actually drawn on the map for the ticked categories.
     *
     * From `layers[category].elements.length`, which is what is shown rather than
     * what was found — when truncated, the drawn count is less than the found total.
     */
    const drawnPowerUnits = useMemo(
        () =>
            power
                ? powerCategories.reduce((sum, category) => {
                      const layer = power.layers[category];
                      return sum + (layer?.elements?.length ?? 0);
                  }, 0)
                : 0,
        [power, powerCategories],
    );

    /**
     * What is on the map, per ticked category — the elements, not the documents.
     *
     * The caption says "dargestellt", and a bridge whose shape is unusable is
     * loaded and then drawn as nothing; counting the load would promise a mark
     * the reader cannot find. Same for the ways, where `total` is what came back.
     */
    const drawnBridges = useMemo(
        () =>
            bridges
                ? bridgeCategories.reduce((sum, category) => {
                      const layer = bridges.layers[category];
                      return sum + (layer?.elements?.length ?? 0);
                  }, 0)
                : 0,
        [bridges, bridgeCategories],
    );

    const drawnWays = useMemo(
        () =>
            ways
                ? wayCategories.reduce((sum, category) => {
                      const layer = ways.layers[category];
                      return sum + (layer?.elements?.length ?? 0);
                  }, 0)
                : 0,
        [ways, wayCategories],
    );

    /**
     * Load the chosen points of interest for the current area.
     *
     * Queried by centre and radius, the same way the building panel does it —
     * the heatmap's area is a polygon, but every kind of area carries a centre,
     * and a radius that covers its extent is close enough for markers. A tile is
     * coloured by geometry; a pin only has to be in the picture.
     */
    useEffect(() => {
        if (poiTypes.length === 0 || !area) {
            setPois([]);
            return undefined;
        }

        // Every kind of area has a centre: the address case carries one, the
        // others get the middle of their bounding box.
        const bounds = area.centre ? null : geometryBounds(area.geometry);
        const centre =
            area.centre ??
            (bounds
                ? { lat: (bounds[0][1] + bounds[1][1]) / 2, lon: (bounds[0][0] + bounds[1][0]) / 2 }
                : null);

        if (!centre) {
            setPois([]);
            return undefined;
        }

        let cancelled = false;

        /**
         * How far around the centre to look.
         *
         * A circle carries its radius. A postal code or a town does not, and the
         * slider's largest step would be far too small for a city — so the corner
         * of its bounding box gives the distance instead: the smallest circle
         * around the centre that still covers the whole area. Degrees to
         * kilometres, with longitude shortened by the latitude.
         */
        let radiusKm = area.radiusKm;
        if (radiusKm === undefined && bounds) {
            const [maxLon, maxLat] = bounds[1];
            const dx = (maxLon - centre.lon) * 111.32 * Math.cos((centre.lat * Math.PI) / 180);
            const dy = (maxLat - centre.lat) * 110.574;
            radiusKm = Math.sqrt(dx * dx + dy * dy);
        }
        const radiusMetres = (radiusKm ?? MAX_RADIUS_KM) * 1000;

        (async () => {
            const found: {
                key: string;
                lat: number;
                lon: number;
                label: string;
                icon: string;
                entity: any;
            }[] = [];

            for (const key of poiTypes) {
                const type = POI_TYPES[key];
                if (!type?.entityType) continue;

                try {
                    const rows: any = await userAPI.query(
                        new Query(
                            new EntityQuery(
                                type.entityType.type,
                                [],
                                undefined,
                                Filter.getContainsPointFilter(centre.lat, centre.lon, radiusMetres),
                            ),
                        ),
                    );

                    (rows ?? []).forEach((row: any) => {
                        if (!Number.isFinite(row?.lat) || !Number.isFinite(row?.lon)) return;
                        found.push({
                            key,
                            lat: row.lat,
                            lon: row.lon,
                            label: row.name || row.id || poiTypeLabel(type, i18n.language),
                            // The document's own marker type where it carries one —
                            // a `PowerUnit` says whether it is a substation or a wind
                            // turbine, and the class alone cannot. The class is the
                            // fallback, not the answer.
                            icon: row.poiType || type.poiIcon,
                            // The whole row rides along: a click opens the side panel,
                            // and the panel is only worth opening if it has the fields
                            // to show. A pair of coordinates is not a point of interest.
                            entity: row,
                        });
                    });
                } catch (error) {
                    // One kind failing must not cost the others: the map is more
                    // useful with three of four layers than with none.
                    console.error(`Could not load points of interest of kind ${key}`, error);
                }
            }

            if (!cancelled) setPois(found);
        })();

        return () => {
            cancelled = true;
        };
    }, [poiTypes, area, i18n.language]);

    /**
     * What the map frames, and when it re-frames.
     *
     * The frame is tied to *which* area is being looked at, not to its size. Turning the radius
     * slider changes the geometry on every step, and handing the map new bounds each time made it
     * refit — the reader's own pan and zoom were undone under their hand, mid-drag. A new address,
     * postal code or town is a new question and re-frames; a wider circle around the same point is
     * the same question, and the view stays where it was put.
     */
    const frameKey = useMemo(() => {
        const atLocations = locations.length > 0 ? `locations:${locations.map((entry) => entry.id).join(',')}` : 'none';
        if (atPlaces) return atLocations;
        if (area?.kind === 'address' && area.centre) {
            return `address:${area.centre.lat.toFixed(5)}:${area.centre.lon.toFixed(5)}`;
        }
        if (area) return `${area.kind}:${area.label}`;
        return atLocations;
    }, [area, locations, atPlaces]);

    const frame = useRef<{ key: string | null; bounds: [[number, number], [number, number]] | null }>({
        key: null,
        bounds: null,
    });

    const mapOptions = useMemo(() => {
        // `geometryBounds` returns a GeoJSON bbox — [lon, lat] per corner, the order the tile
        // endpoint speaks. Leaflet's fitBounds reads [lat, lon], and handing it the GeoJSON order
        // puts Frankfurt in Central Asia, so the swap happens here, at the boundary between the two
        // conventions, rather than by quietly changing what geometryBounds means.
        // An den Liegenschaften gerechnet rahmen die Liegenschaften, nicht das Gebiet.
        const bbox = area && !atPlaces ? geometryBounds(area.geometry) : null;

        // Frame whatever is on the map. With only a project selected there is no area, and without
        // bounds the map would stay wherever it opened while the markers sit off-screen.
        const lats: number[] = [];
        const lons: number[] = [];
        if (bbox) {
            lons.push(bbox[0][0], bbox[1][0]);
            lats.push(bbox[0][1], bbox[1][1]);
        }
        // Only when no area is chosen do the markers set the frame. Once there is an area, that is
        // what the reader asked to look at — folding thirty properties across a Bundesland into the
        // same box would zoom a 1 km circle down to a dot.
        if (!bbox) {
            locations.forEach((location) => {
                lats.push(location.lat);
                lons.push(location.lon);
            });
        }

        // A single marker has no extent; pad it so fitBounds does not zoom to the maximum.
        const padding = lats.length === 1 ? 0.01 : 0;
        const computed =
            lats.length > 0
                ? ([
                      [Math.min(...lats) - padding, Math.min(...lons) - padding],
                      [Math.max(...lats) + padding, Math.max(...lons) + padding],
                  ] as [[number, number], [number, number]])
                : null;

        // The same array instance for as long as the question is the same: the map refits on a new
        // `bounds` reference, so handing back an equal-but-new one would re-frame on every render.
        if (frame.current.key !== frameKey) {
            frame.current = { key: frameKey, bounds: computed };
        }
        // Ein angeklickter Treffer rückt ins Bild, sonst die Treffer, sonst das Gebiet.
        const bounds = (mode === 'search' && (focusBounds || hitsFrame)) || frame.current.bounds;

        return {
            ...(bounds ? { bounds } : {}),
            ...(tiles && tiles.scale.length > 0
                ? {
                      legend: {
                          title: `${tiles.themeLabel}${metric?.category && metric.categoryLabel ? ` — ${metric.categoryLabel}` : ''}`,
                      },
                      heatmap: { categories: tiles.scale },
                  }
                : {}),
        };
        // `frameKey` is what decides re-framing; `area` and `locations` only feed the computation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area, tiles, metric, locations, frameKey, mode, focusBounds, hitsFrame, atPlaces]);

    useEffect(() => {
        // The project belongs in the title because it qualifies everything below it: the same map
        // with a different project chosen is a different answer, and the header is where the reader
        // already looks to know what they are looking at.
        setTitle(project?.name ? `${t('heatmap.title')} · ${project.name}` : t('heatmap.title'));
    }, [setTitle, t, project?.name]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {/* Narrow windows stack the chimney above the map rather than squeezing both; from lg
                the two columns share the height and scroll independently. */}
            <div
                className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-y-auto p-3 lg:grid-cols-[260px_minmax(0,1fr)] lg:overflow-hidden xl:grid-cols-[320px_minmax(0,1fr)]"
                style={{ background: pageBackground(brand) }}
            >
                <div className="flex flex-col gap-3 lg:min-h-0 lg:overflow-y-auto lg:pr-0.5">
                    {/* No card of its own: the project is named in the page title and drawn on the
                        map, so this only speaks up when one of the two is missing. */}
                    {/* Unter CBOS steht die Projektauswahl in der Seitenleiste; im Dashboard gibt es die nicht,
                        dort steht sie hier oben. */}
                    <div role="tablist" className="grid grid-cols-2 gap-1 rounded-xl border bg-card p-1 shadow-sm">
                        {(['heatmap', 'search'] as const).map((key) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={mode === key}
                                className={cn(
                                    'rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                                    mode === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent',
                                )}
                                onClick={() => setMode(key)}
                            >
                                {t(`heatmap.tabs.${key}`)}
                            </button>
                        ))}
                    </div>
                    {/* Das Projekt gehört zur Heatmap; die Grundstückssuche braucht keins. */}
                    {mode === 'heatmap' && !inCbos && <CbosProjectPicker />}
                    {mode === 'heatmap' && <ProjectSection selectedProjectId={selectedProjectId} onLocations={handleLocations} />}

                    <Section title={t('heatmap.area.title')}>
                        <AreaSection area={area} onAreaChange={setArea} />
                    </Section>

                    {mode === 'search' && (
                        <Section title={t('heatmap.search.title')}>
                            <SearchSection hasArea={!!area} searching={searching} onSearch={runSearch} />
                        </Section>
                    )}

                    {mode === 'heatmap' && (<>
                    <Section title={t('heatmap.metrics.title')}>
                        <MetricsSection
                            selected={metric}
                            onSelect={(next) => {
                                setMetric(next);
                                // One picture at a time: a Zensus metric colours the grid, an
                                // INKAR indicator colours whole administrative units, a score
                                // colours the grid from a formula. Two of them at once is no
                                // picture at all.
                                if (next) {
                                    setSelectedScoreId(null);
                                    setInkarSelection(null);
                                }
                            }}
                            inkarSelected={inkarSelection}
                            onSelectInkar={(next) => {
                                setInkarSelection(next);
                                if (next) {
                                    setMetric(null);
                                    setSelectedScoreId(null);
                                }
                            }}
                            parcelsShown={showParcels}
                            onToggleParcels={setShowParcels}
                            parcelsBrw={parcelsBrw}
                            onToggleParcelsBrw={setParcelsBrw}
                            brwZonesShown={showBrwZones}
                            onToggleBrwZones={setShowBrwZones}
                            landUseShown={showLandUse}
                            onToggleLandUse={setShowLandUse}
                            selectedPowerCategories={powerCategories}
                            onTogglePowerCategory={(key) =>
                                setPowerCategories((previous) =>
                                    previous.includes(key)
                                        ? previous.filter((entry) => entry !== key)
                                        : [...previous, key],
                                )
                            }
                            selectedWayCategories={wayCategories}
                            onToggleWayCategory={(key) =>
                                setWayCategories((previous) =>
                                    previous.includes(key)
                                        ? previous.filter((entry) => entry !== key)
                                        : [...previous, key],
                                )
                            }
                            selectedBridgeCategories={bridgeCategories}
                            onToggleBridgeCategory={(key) =>
                                setBridgeCategories((previous) =>
                                    previous.includes(key)
                                        ? previous.filter((entry) => entry !== key)
                                        : [...previous, key],
                                )
                            }
                            earthquakesShown={showEarthquakes}
                            onToggleEarthquakes={setShowEarthquakes}
                            siteHazardsShown={siteHazards}
                            onToggleSiteHazard={(key) =>
                                setSiteHazards((previous) =>
                                    previous.includes(key)
                                        ? previous.filter((entry) => entry !== key)
                                        : [...previous, key],
                                )
                            }
                            onClearAll={clearSelection}
                            selectedPoiTypes={poiTypes}
                            onTogglePoiType={(key) =>
                                setPoiTypes((previous) =>
                                    previous.includes(key)
                                        ? previous.filter((entry) => entry !== key)
                                        : [...previous, key],
                                )
                            }
                        />
                        {/* Ticking a kind with no area chosen is a silent no-op
                            otherwise: the query needs a centre, and the reader is
                            left wondering why nothing appeared. */}
                        {poiTypes.length > 0 && !area ? (
                            <div className={sectionHint}>{t('heatmap.pois.needsArea')}</div>
                        ) : poiTypes.length > 0 ? (
                            <div className={sectionHint}>
                                {t('heatmap.pois.count', {
                                    count: pois.filter((poi) => poiTypes.includes(poi.key)).length,
                                })}
                            </div>
                        ) : (
                            <></>
                        )}
                        {showParcels ? (
                            <div className={sectionHint}>
                                {loadingParcels
                                    ? `${t('heatmap.parcels.loading')} ...`
                                    : parcelError
                                      ? parcelError
                                      : !area
                                        ? t('heatmap.parcels.needsArea')
                                        : parcels
                                          ? t(
                                                parcels.truncated
                                                    ? 'heatmap.parcels.truncated'
                                                    : 'heatmap.parcels.count',
                                                { count: parcels.parcels.length },
                                            )
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {showBrwZones ? (
                            <div className={sectionHint}>
                                {loadingBrwZones
                                    ? `${t('heatmap.brwZones.loading')} ...`
                                    : brwZonesError
                                      ? brwZonesError
                                      : !area
                                        ? t('heatmap.brwZones.needsArea')
                                        : brwZones
                                          ? t(
                                                brwZones.truncated
                                                    ? 'heatmap.brwZones.truncated'
                                                    : 'heatmap.brwZones.count',
                                                { count: brwZones.zones.length },
                                            )
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {inkarSelection ? (
                            <div className={sectionHint}>
                                {loadingInkar
                                    ? `${t('heatmap.inkar.loading')} ...`
                                    : inkarError
                                      ? inkarError
                                      : !area
                                        ? t('heatmap.inkar.needsArea')
                                        : inkar && inkar.unitCount > 0
                                          ? `${t('heatmap.inkar.count', {
                                                units: levelSummary(inkar.levels, i18n.language),
                                                year: inkar.year ?? '',
                                            })}${inkar.truncated ? ` ${t('heatmap.inkar.truncated')}` : ''}`
                                          : inkar
                                            ? t('heatmap.inkar.none')
                                            : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {bridgeCategories.length > 0 ? (
                            <div className={sectionHint}>
                                {loadingBridges
                                    ? `${t('heatmap.bridges.loading')} ...`
                                    : bridgeError
                                      ? bridgeError
                                      : !area
                                        ? t('heatmap.bridges.needsArea')
                                        : bridges
                                          ? `${t('heatmap.bridges.count', {
                                                count: drawnBridges,
                                            })}${bridges.truncated ? ` ${t('heatmap.bridges.truncated')}` : ''}`
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {wayCategories.length > 0 ? (
                            <div className={sectionHint}>
                                {loadingWays
                                    ? `${t('heatmap.ways.loading')} ...`
                                    : wayError
                                      ? wayError
                                      : !area
                                        ? t('heatmap.ways.needsArea')
                                        : ways
                                          ? `${t('heatmap.ways.count', { count: drawnWays })}${
                                                ways.truncated ? ` ${t('heatmap.ways.truncated')}` : ''
                                            }`
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {powerCategories.length > 0 ? (
                            <div className={sectionHint}>
                                {loadingPower
                                    ? `${t('heatmap.power.loading')} ...`
                                    : powerError
                                      ? powerError
                                      : !area
                                        ? t('heatmap.power.needsArea')
                                        : power
                                          ? `${t('heatmap.power.count', { count: drawnPowerUnits })}${
                                                power.truncated ? ` ${t('heatmap.power.truncated')}` : ''
                                            }`
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {siteHazards.map((key) => {
                            const result = siteHazardResults[key];
                            const text = siteHazardLoading.includes(key)
                                ? `${t(`heatmap.siteHazards.${key}.loading`)} ...`
                                : siteHazardErrors[key]
                                  ? siteHazardErrors[key]
                                  : !area
                                    ? t('heatmap.earthquakes.needsArea')
                                    : !result
                                      ? ''
                                      : key === 'pluvial'
                                        ? `${t('heatmap.siteHazards.pluvial.count', {
                                              wet: result.wetCells ?? 0,
                                              cells: result.cells ?? 0,
                                              noData: result.noDataCells ?? 0,
                                          })}${result.clipped ? ` ${t('heatmap.siteHazards.pluvial.clipped')}` : ''}`
                                        : key === 'climate'
                                          ? `${t('heatmap.siteHazards.climate.count', {
                                                label: result.label ?? '',
                                                period: result.period ?? '',
                                                count: result.cells ?? 0,
                                            })}${result.clipped ? ` ${t('heatmap.siteHazards.climate.clipped')}` : ''}`
                                          : key === 'species'
                                            ? `${t('heatmap.siteHazards.species.count', { count: result.count ?? 0 })}${
                                                  result.unrecorded ? ` ${t('heatmap.siteHazards.species.unrecorded', { count: result.unrecorded })}` : ''
                                              }`
                                            : `${t(`heatmap.siteHazards.${key}.count`, { count: result.count ?? 0 })}${
                                              result.truncated ? ` ${t(`heatmap.siteHazards.${key}.truncated`)}` : ''
                                          }`;
                            return text ? (
                                <div key={key} className={sectionHint}>
                                    {text}
                                </div>
                            ) : null;
                        })}
                        {showEarthquakes ? (
                            <div className={sectionHint}>
                                {loadingEarthquakes
                                    ? `${t('heatmap.earthquakes.loading')} ...`
                                    : earthquakeError
                                      ? earthquakeError
                                      : !area
                                        ? t('heatmap.earthquakes.needsArea')
                                        : earthquakes
                                          ? `${t('heatmap.earthquakes.count', {
                                                count: earthquakes.count,
                                            })}${
                                                earthquakes.strongest !== null
                                                    ? ` ${t('heatmap.earthquakes.strongest', {
                                                          magnitude: earthquakes.strongest.toFixed(1),
                                                      })}`
                                                    : ''
                                            }`
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                        {showLandUse ? (
                            <div className={sectionHint}>
                                {loadingLandUse
                                    ? `${t('heatmap.landUse.loading')} ...`
                                    : landUseError
                                      ? landUseError
                                      : !area
                                        ? t('heatmap.landUse.needsArea')
                                        : landUse
                                          ? t(
                                                landUse.truncated
                                                    ? 'heatmap.landUse.truncated'
                                                    : 'heatmap.landUse.count',
                                                { count: landUse.areas.length },
                                            )
                                          : ''}
                            </div>
                        ) : (
                            <></>
                        )}
                    </Section>

                    <Section title={t('heatmap.scores.title')}>
                        <ScoreSection
                            scores={scores}
                            onScoresChange={setScores}
                            selectedScoreId={selectedScoreId}
                            onSelect={(id) => {
                                setSelectedScoreId(id);
                                // A different score has different knobs; carrying the old values
                                // over would apply one score's setting to another's parameter.
                                setParameterValues({});
                                // The map shows one thing at a time, and a score *is* the thing
                                // rather than a layer over a metric.
                                if (id) {
                                    setMetric(null);
                                    setInkarSelection(null);
                                }
                            }}
                            catalog={catalog}
                            projectLocationCount={locations.length}
                            onlyProject={onlyProject}
                            onOnlyProjectChange={setOnlyProject}
                            parameterValues={parameterValues}
                            onParameterChange={(name, value) =>
                                setParameterValues((current) => ({ ...current, [name]: value }))
                            }
                        />
                    </Section>
                    </>)}
                </div>

                <div className="flex min-w-0 flex-col gap-2 lg:min-h-0">
                    <div className="flex min-h-6 items-center px-1 text-muted-foreground">
                        {mode === 'search' ? (
                            searching ? (
                                <span role="status" className="inline-flex items-center gap-2 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                    {t('heatmap.search.searching')} …
                                </span>
                            ) : searchError ? (
                                <span className="text-sm text-destructive">{searchError}</span>
                            ) : searchResult && !searchResult.indexed ? (
                                <span className="text-sm">{t('heatmap.search.notIndexed')}</span>
                            ) : searchResult && searchResult.total === 0 ? (
                                <span className="text-sm">{t('heatmap.search.none')}</span>
                            ) : (
                                <span className="text-sm">{searchResult ? '' : t('heatmap.search.hint')}</span>
                            )
                        ) : loadingTiles ? (
                            <span role="status" className="inline-flex items-center gap-2 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                {t('heatmap.loadingTiles')} …
                            </span>
                        ) : tileError ? (
                            <span className="text-sm text-destructive">{tileError}</span>
                        ) : tiles ? (
                            <span className="text-sm">
                                {t(`heatmap.scores.unitSummary.${(tiles as ScoreResult).granularity ?? 'cell'}`, {
                                    count: tiles.tileCount,
                                    metric: tiles.themeLabel,
                                    year: tiles.year ?? '',
                                })}
                                {tiles.truncated ? ` — ${t('heatmap.truncated')}` : ''}
                                {scoreGaps(tiles as ScoreResult, t)}
                            </span>
                        ) : (
                            <span className="text-sm">{t('heatmap.pickAreaAndMetric')}</span>
                        )}
                    </div>

                    <div
                        className="relative min-h-[320px] flex-1 overflow-hidden rounded-2xl border bg-card shadow-sm"
                        ref={frameRef}
                    >
                        {tiles && tiles.scale.length > 0 ? (
                            <div className={cn(LEGEND, 'bottom-6 left-3.5')}>
                                <div className={LEGEND_TITLE}>
                                    {tiles.themeLabel}
                                    {tiles.category !== '__total__' ? ` — ${tiles.categoryLabel}` : ''}
                                </div>
                                {tiles.scale.map((category) => (
                                    <div key={category.label} className={LEGEND_ROW}>
                                        <span
                                            className={LEGEND_SWATCH}
                                            style={{ backgroundColor: category.color }}
                                        />
                                        <span>{category.label}</span>
                                    </div>
                                ))}
                                <div className="mt-1.5 text-[10px] text-muted-foreground">
                                    {(tiles as ScoreResult).granularity && (tiles as ScoreResult).granularity !== 'cell'
                                        ? t(`heatmap.scores.legendFoot.${(tiles as ScoreResult).granularity}`)
                                        : t('heatmap.legendFoot', {
                                              year: tiles.year ?? '',
                                              resolution: tiles.resolutionM,
                                          })}
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* The indicator's own scale, in the place the metric legend would be —
                            the two are never on at once. */}
                        {inkar && inkar.scale.length > 0 ? (
                            <div className="pointer-events-none absolute bottom-[22px] left-[14px] z-[500] min-w-[150px] rounded-xl border border-[rgba(0,29,52,0.12)] bg-[rgba(255,255,255,0.94)] px-3 py-2.5 shadow-[0_4px_14px_rgba(0,29,52,0.18)]">
                                <div className="mb-1.5 max-w-[220px] font-heading text-[11px] font-bold text-[#001d34]">
                                    {inkar.label}
                                    {inkar.year ? ` (${inkar.year})` : ''}
                                </div>
                                {inkar.scale.map((category) => (
                                    <div key={category.label} className="flex items-center gap-[7px] py-px text-[11px] text-[#001d34]">
                                        <span
                                            className="h-3.5 w-3.5 shrink-0 rounded-[3px] border border-[rgba(0,29,52,0.15)]"
                                            style={{ backgroundColor: category.color }}
                                        />
                                        <span>{category.label}</span>
                                    </div>
                                ))}
                                <div className="mt-1.5 text-[10px] text-[rgba(0,29,52,0.62)]">
                                    {t('heatmap.inkar.legendFoot', {
                                        level: levelNames(inkar.levels, i18n.language),
                                    })}
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* The magnitude bands, as the chat answer draws them. Strongest
                            first, which is the order they are matched in. */}
                        {earthquakes && earthquakes.elements.length > 0 ? (
                            // Top right, because the land use legend already has the bottom right
                            // and the two can be on together.
                            <div className={cn(LEGEND, 'right-3.5 top-3.5')}>
                                <div className={LEGEND_TITLE}>{t('heatmap.earthquakes.legend')}</div>
                                {earthquakes.scale.map((band) => (
                                    <div key={band.min} className={LEGEND_ROW}>
                                        <span
                                            className={LEGEND_SWATCH}
                                            style={{ backgroundColor: band.color }}
                                        />
                                        <span>{t('heatmap.earthquakes.legendBand', { min: band.min })}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* Top left, the one free corner — the zoom control is switched off on this
                            map, the metric scale sits bottom left, land use bottom right and the
                            magnitude bands top right. Only while the mode is on: a key to a colour
                            nothing is painted in is furniture. */}
                        {(parcelsBrw && parcels && parcels.parcels.length > 0) ||
                        (showBrwZones && brwZones && brwZones.zones.length > 0) ? (
                            <div className={cn(LEGEND, 'left-3.5 top-3.5')}>
                                <div className={LEGEND_TITLE}>{t('heatmap.parcels.brwLegend')}</div>
                                {/* Highest first. The ramp runs cheap-to-expensive, but a legend
                                    read top-down should agree with the map read at a glance, and
                                    the expensive plots are what the eye goes to. */}
                                {[...brwLegend()].reverse().map((step) => (
                                    <div key={step.label} className={LEGEND_ROW}>
                                        <span
                                            className={LEGEND_SWATCH}
                                            style={{ backgroundColor: step.color }}
                                        />
                                        <span>{step.label}</span>
                                    </div>
                                ))}
                                {/* The grey is not a value on the ramp, it is the absence of a
                                    single one — several Bodenrichtwerte apply to that plot and
                                    which one holds depends on the plot itself. Without this row a
                                    reader takes grey for "no data". */}
                                <div className={cn(LEGEND_ROW, 'mt-1.5 border-t pt-1.5')}>
                                    <span
                                        className={LEGEND_SWATCH}
                                        style={{ backgroundColor: AMBIGUOUS_COLOR }}
                                    />
                                    <span>{t('heatmap.parcels.brwAmbiguous')}</span>
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}
                        {/* Only what is on screen: the vocabulary has 31 categories and a single
                            view rarely shows a third of them. A legend listing the rest would be
                            a lookup table, not a key to this picture. */}
                        {landUse && landUse.categories.length > 0 ? (
                            // Opposite the metric legend: both can be on at once, and a box on top
                            // of the other would hide whichever was drawn second. Scrollable and
                            // therefore clickable — a dense area shows many of the 31 entries.
                            <div
                                className={cn(
                                    LEGEND,
                                    'pointer-events-auto bottom-6 right-3.5 max-h-[45%] overflow-y-auto',
                                )}
                            >
                                <div className={LEGEND_TITLE}>{t('heatmap.landUse.label')}</div>
                                {landUse.categories.map((category) => (
                                    <div key={category} className={LEGEND_ROW}>
                                        <span
                                            className={LEGEND_SWATCH}
                                            style={{ backgroundColor: landUseColor(category) }}
                                        />
                                        <span>{landUseLabel(category, i18n.language)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <></>
                        )}
                        <Map
                            height={mapHeight}
                            query={null}
                            data={mapData}
                            options={mapOptions}
                            setObject={handleMapClick}
                            hideOverlay
                        ></Map>
                    </div>
                    {mode === 'search' && searchResult && searchResult.rows.length > 0 && (
                        <div className="flex h-[38%] min-h-[220px] flex-col">
                            <SearchResultsTable
                                rows={searchResult.rows}
                                total={searchResult.total}
                                totalCapped={searchResult.totalCapped}
                                selectedId={selectedHitId}
                                loadingMore={searchingMore}
                                onSelect={selectHit}
                                onMore={searchMore}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }: { title: string; children: any }) {
    return (
        <Card>
            <div className="flex min-h-[52px] items-center border-b px-3 py-2 text-xs font-bold uppercase tracking-[0.09em] text-muted-foreground">
                {title}
            </div>
            <div className="p-3.5">{children}</div>
        </Card>
    );
}
